import React from "react";
import V2Icon from "../icon/v2_Icon";
import "./v2_Button.css";

/**
 * Icon-only button with consistent sizing and shape variants.
 *
 * Props:
 * - name: Icon name from Material Symbols (if provided, renders V2Icon component)
 * - variant: button variant (secondary, subtle, primary, brand, danger, success)
 * - shape: button shape (square, circle); default: "square"
 * - className: additional CSS classes
 * - children: content to render (used if name prop is not provided)
 *
 * Variants:
 * - secondary: default (neutral surface)
 * - subtle: transparent background, low emphasis
 * - primary: brand-bold background
 * - brand: brand-boldest background
 * - danger: destructive action
 * - success: positive/approve action
 * - selected: selection state (uses --background-selected-default)
 *
 * Shape Options:
 * - square: Default square button with 4px rounded corners
 * - circle: Full circular button (50% border-radius) with visible border
 *
 * Usage:
 * - Square (default): <V2IconButton name="edit" />
 * - Circular: <V2IconButton name="edit" shape="circle" />
 * - With variant: <V2IconButton name="delete" variant="danger" shape="circle" />
 * - With children: <V2IconButton><CustomIcon /></V2IconButton>
 */
const V2IconButton = React.forwardRef(function V2IconButton(
  { variant = "secondary", shape = "square", className = "", name, children, ...props },
  ref
) {
  const classes = ["v2-icon-btn", `v2-icon-btn--${variant}`, `v2-icon-btn--${shape}`, className]
    .filter(Boolean)
    .join(" ");

  return (
    <button ref={ref} type="button" className={classes} {...props}>
      {name ? <V2Icon name={name} /> : children}
    </button>
  );
});

export default V2IconButton;
